import React from 'react';
import { useTranslation } from 'react-i18next';
import { Card, Descriptions, Divider } from 'antd';
import Datestamp from 'vl-common/src/components/Datestamp';
import type { AppointmentDetails } from 'store/actions/clinicians';
import ReadOnlyNotes from './ReadOnlyNotes';

const Factsheets = ({ factsheets }: { factsheets: AppointmentDetails['factsheets'] }) => {
  const { t } = useTranslation('common');
  if (factsheets?.length > 0) {
    return (
      <>
        {factsheets?.map((factsheet, index) => (
          <span key={factsheet.factsheet_id}>
            {index > 0 && <>, </>}
            <a target="_blank" rel="noreferrer" href={factsheet?.factsheet_url}>
              {factsheet?.factsheet_name}
            </a>
          </span>
        ))}
      </>
    );
  }
  return <span style={{ fontStyle: 'italic' }}>{t('No Factsheets')}</span>;
};

export const PreviousAppointmentNotes = ({ appointment_info }: { appointment_info: AppointmentDetails }) => {
  const { t } = useTranslation('common');
  return (
    <Card style={{ marginBottom: 10 }}>
      <Descriptions
        title={<span style={{ fontSize: 13, fontWeight: 500 }}>{t('APPOINTMENT INFO')}</span>}
        column={3}
        labelStyle={{ fontWeight: 600 }}
      >
        <Descriptions.Item label={t('Appointment ID')}>{appointment_info.appt_id}</Descriptions.Item>
        <Descriptions.Item label={t('Clinician Name')}>{appointment_info.clinician_name}</Descriptions.Item>
        <Descriptions.Item label={t('Role')}>{appointment_info.clinician_subtype_desc}</Descriptions.Item>
        <Descriptions.Item label={t('Appointment Type')}>{appointment_info.appt_type_desc}</Descriptions.Item>
        <Descriptions.Item label={t('Date Of Appointment')}>
          {appointment_info.appt_completed_date && (
            <Datestamp
              date={appointment_info.appt_completed_date as unknown as Date}
              format="Date @ Time (long, 24h) - Friday, March 3rd, 2022 @ 08:57"
            />
          )}
        </Descriptions.Item>
      </Descriptions>
      <Divider />
      <Descriptions
        title={<span style={{ fontSize: 13, fontWeight: 500 }}>{t('APPOINTMENT NOTES')}</span>}
        column={2}
        layout="vertical"
        labelStyle={{ fontWeight: 600 }}
      >
        {appointment_info?.notes?.map((note) => (
          <Descriptions.Item key={note?.note_code} contentStyle={{ fontStyle: 'italic' }} label={note?.note_desc}>
            <ReadOnlyNotes value={note.note_text || undefined} placeholder={t('No notes')} />
          </Descriptions.Item>
        ))}
      </Descriptions>
      <Divider />
      <Descriptions
        title={<span style={{ fontSize: 13, fontWeight: 500 }}>{t('APPOINTMENT OUTCOME')}</span>}
        column={2}
        labelStyle={{ fontWeight: 600 }}
      >
        <Descriptions.Item label={t('ICD Diagnosis')}>
          {appointment_info.icd.length ? (
            <ul style={{ listStyle: 'none' }}>
              {appointment_info.icd.map((diagnosis) => (
                <li key={diagnosis.icd_code}>
                  <strong>{diagnosis.icd_code}</strong> — {diagnosis.icd_desc}
                </li>
              ))}
            </ul>
          ) : (
            <span style={{ fontStyle: 'italic' }}>{t('No Diagnosis')}</span>
          )}
        </Descriptions.Item>
        <Descriptions.Item label={t('GeneralAdvice')}>
          {appointment_info?.genadv?.length > 0 ? (
            appointment_info?.genadv?.map((advice) => advice?.genadv_desc)?.join(', ')
          ) : (
            <span style={{ fontStyle: 'italic' }}>{t('No General Advice Given')}</span>
          )}
        </Descriptions.Item>
        <Descriptions.Item label={t('DiagnosisFactsheet')}>
          <Factsheets factsheets={appointment_info?.factsheets} />
        </Descriptions.Item>
        <Descriptions.Item label={t('Appointment Type')}>{appointment_info.appt_type_desc}</Descriptions.Item>
      </Descriptions>
    </Card>
  );
};
